import { useEffect } from 'react';

export interface HotkeyOptions {
  /** When false, the listener is registered but the handler short-circuits. */
  enabled?: boolean;
  /** When false, hits inside input/textarea/contenteditable are ignored. */
  ignoreInInputs?: boolean;
}

/**
 * Bind a global keydown handler. Supported combos:
 *   - `'Mod+k'`     → Cmd-K on Mac, Ctrl-K elsewhere
 *   - `'Escape'`    → just Esc
 *   - `'Mod+Enter'` → Cmd/Ctrl + Enter
 *
 * The handler runs on capture phase so it fires even when other listeners
 * try to stop propagation downstream. Always calls `preventDefault()` when
 * matched so the browser doesn't intercept Cmd-K's default behavior.
 *
 * `ignoreInInputs` defaults to FALSE — Cmd-K should override input focus
 * (matches Linear/Notion/Slack). Set to true if you want the hotkey to
 * skip when the user is mid-typing in another input.
 */
export function useGlobalHotkey(
  combo: string,
  handler: (event: KeyboardEvent) => void,
  options: HotkeyOptions = {}
): void {
  const { enabled = true, ignoreInInputs = false } = options;

  useEffect(() => {
    if (!enabled) return;

    const isMac = typeof navigator !== 'undefined' && /mac|iphone|ipad|ipod/i.test(navigator.platform);
    // Parse the combo once per effect run.
    const tokens = combo.split('+').map((t) => t.trim());
    const wantMod = tokens.includes('Mod');
    const targetKey = tokens[tokens.length - 1].toLowerCase();

    const listener = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== targetKey) return;
      if (wantMod) {
        const modPressed = isMac ? event.metaKey : event.ctrlKey;
        if (!modPressed) return;
      } else {
        if (event.metaKey || event.ctrlKey) return;
      }
      if (ignoreInInputs) {
        const target = event.target as HTMLElement | null;
        const tag = target?.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;
      }
      event.preventDefault();
      handler(event);
    };

    document.addEventListener('keydown', listener, { capture: true });
    return () => document.removeEventListener('keydown', listener, { capture: true } as EventListenerOptions);
  }, [combo, handler, enabled, ignoreInInputs]);
}
